/**
 * @file modules/billing/returnTo.js
 * @description Where a payment should hand somebody back to.
 *
 * After paying or upgrading, the next thing is the thing that sent them — the
 * paywall, the report, the review request, the area that ran out. Every upgrade
 * link used to be a bare `/billing?tab=plans`, so the return address was lost
 * the moment the link was pressed and a member who had just paid landed on
 * the Activity tab. These build the link with the address, and read it back
 * safely on the other side.
 */
import { requiredAccess, ACCESS } from '../../config/plans.js';

/** An in-app path, or null. Never another origin (`//evil`, `https://…`). */
export function safeReturnTo(value) {
  const s = typeof value === 'string' ? value.trim() : '';
  if (!s.startsWith('/') || s.startsWith('//') || s.startsWith('/\\')) return null;
  return s;
}

/** The page somebody is on now, as a return address. */
export function hereAsReturnTo(location) {
  if (!location) return null;
  return safeReturnTo(`${location.pathname || ''}${location.search || ''}${location.hash || ''}`);
}

/** The billing plan id that satisfies a gate's requirement (plan id, tier or legacy alias). */
export function planForRequirement(requirement) {
  const level = requiredAccess(requirement);
  if (level >= ACCESS.ORG) return 'max';
  if (level >= ACCESS.INDIVIDUAL) return 'pro';
  return null;
}

/**
 * The upgrade link: the plan comparison, with the plan the gate needs chosen
 * and the page to return to once the plan is confirmed.
 *
 *   upgradeHref({ returnTo: '/requirements/v2/p1/report', plan: 'pro' })
 *   → '/billing?tab=plans&plan=pro&returnTo=%2Frequirements%2Fv2%2Fp1%2Freport'
 */
export function upgradeHref({ returnTo = null, plan = null } = {}) {
  const params = new URLSearchParams({ tab: 'plans' });
  const planId = plan ? planForRequirement(plan) : null;
  if (planId) params.set('plan', planId);
  const back = safeReturnTo(returnTo);
  // Not the plan page itself — returning there is staying put. Other billing
  // pages (the Areas gate at /billing/areas) are a real place to go back to.
  const backPath = back ? back.split(/[?#]/)[0].replace(/\/+$/, '') : '';
  if (back && backPath !== '/billing') params.set('returnTo', back);
  return `/billing?${params.toString()}`;
}

/** The area-pack page, remembering the Blueprint or area that ran out. */
export function areaPackHref({ returnTo = null } = {}) {
  const back = safeReturnTo(returnTo);
  return back ? `/billing/areas?returnTo=${encodeURIComponent(back)}` : '/billing/areas';
}

/**
 * The public pricing card's link. Somebody signed out goes through sign-up and
 * arrives at the plan they chose; somebody signed in goes straight there.
 */
export function choosePlanHref({ plan, signedIn = false, returnTo = null } = {}) {
  const target = upgradeHref({ plan, returnTo });
  return signedIn ? target : `/register?next=${encodeURIComponent(target)}`;
}
